import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  uGet as aGet,
  uPost as aPost,
  uPut as aPut,
} from "../../utils/userApi";
import {
  C,
  Input,
  Select,
  Btn,
  Modal,
  EmptyState,
  LoadingSpinner,
} from "./AdminPanel";
import {
  FiSearch,
  FiEdit2,
  FiUserPlus,
  FiShield,
  FiUserX,
  FiRefreshCw,
  FiKey,
  FiUser,
  FiMail,
  FiPhone,
  FiLock,
  FiCheck,
  FiX,
  FiMoreVertical,
  FiClock,
  FiCalendar,
  FiAward,
  FiTrendingUp,
  FiFilter,
} from "react-icons/fi";
import {
  FaCrown,
  FaUserShield,
  FaUserTie,
  FaRegUserCircle,
  FaChartLine,
} from "react-icons/fa";

const ROLE_HIERARCHY = { user: 0, employee: 1, admin: 2, superadmin: 3 };

const ROLE_META = {
  superadmin: {
    label: "Super Admin",
    icon: <FaUserShield />,
    color: "#f59e0b",
    bg: "linear-gradient(135deg, #f59e0b15, #f9731615)",
    description:
      "Full access to everything including user and staff management",
    permissions: [
      "Manage all",
      "User management",
      "Staff management",
      "System settings",
    ],
  },
  admin: {
    label: "Admin",
    icon: <FaCrown />,
    color: "#6366f1",
    bg: "linear-gradient(135deg, #6366f115, #8b5cf615)",
    description: "Full access except user management of higher roles",
    permissions: [
      "Product management",
      "Order management",
      "Staff management (limited)",
      "Reports",
    ],
  },
  employee: {
    label: "Employee",
    icon: <FaUserTie />,
    color: "#10b981",
    bg: "linear-gradient(135deg, #10b98115, #34d39915)",
    description: "Can view orders, messages and dashboard only",
    permissions: [
      "View orders",
      "View dashboard",
      "Process orders",
      "Customer support",
    ],
  },
};

function RoleBadge({ role, size = "sm" }) {
  const m = ROLE_META[role];
  if (!m) return null;
  return (
    <div className={`role-badge-enhanced ${role} ${size}`}>
      <span className="role-icon">{m.icon}</span>
      <span className="role-label">{m.label}</span>
    </div>
  );
}

function PermissionBadge({ permission }) {
  return (
    <span className="permission-badge-enhanced">
      <FiCheck size={10} />
      {permission}
    </span>
  );
}

export default function Staff({ toast, user: currentUser }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [resetPwd, setResetPwd] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const debounceRef = useRef();

  const load = useCallback(() => {
    setLoading(true);
    aGet("/users/staff/list", { search })
      .then((d) => setStaff(d.staff))
      .catch((e) => toast(e.message, "error"))
      .finally(() => setLoading(false));
  }, [search, toast]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 350);
  }, [load]);

  const filteredStaff = useMemo(() => {
    let filtered = [...staff];
    if (roleFilter !== "all") {
      filtered = filtered.filter((s) => s.role === roleFilter);
    }
    return filtered;
  }, [staff, roleFilter]);

  const stats = useMemo(() => {
    return {
      total: staff.length,
      superadmin: staff.filter((s) => s.role === "superadmin").length,
      admin: staff.filter((s) => s.role === "admin").length,
      employee: staff.filter((s) => s.role === "employee").length,
    };
  }, [staff]);

  const handleRevoke = async (member) => {
    if (
      !window.confirm(
        `Revoke staff access for ${member.name}? They will become a regular user.`,
      )
    )
      return;
    try {
      await aPut(`/users/staff/${member._id}/revoke`);
      toast(`Access revoked for ${member.name}`);
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const assignableRoles = Object.keys(ROLE_META).filter(
    (r) => ROLE_HIERARCHY[r] <= ROLE_HIERARCHY[currentUser?.role],
  );

  const clearFilters = () => {
    setSearch("");
    setRoleFilter("all");
  };

  return (
    <div className="staff-container-enhanced">
      {/* Header */}
      <div className="staff-header-enhanced">
        {/* <div className="header-title-section">
          <div className="header-icon">👥</div>
          <div>
            <h1 className="header-title">Staff Management</h1>
            <p className="header-subtitle">
              Manage team members, roles, and permissions
            </p>
          </div>
        </div> */}
        <div className="header-stats">
          <div className="stat-card-mini">
            <FaRegUserCircle />
            <div>
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total Staff</span>
            </div>
          </div>
          <div className="stat-card-mini superadmin">
            <FaUserShield />
            <div>
              <span className="stat-value">{stats.superadmin}</span>
              <span className="stat-label">Super Admins</span>
            </div>
          </div>
          <div className="stat-card-mini admin">
            <FaCrown />
            <div>
              <span className="stat-value">{stats.admin}</span>
              <span className="stat-label">Admins</span>
            </div>
          </div>
          <div className="stat-card-mini employee">
            <FaUserTie />
            <div>
              <span className="stat-value">{stats.employee}</span>
              <span className="stat-label">Employees</span>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="staff-toolbar-enhanced">
        <div className="toolbar-left">
          <div className="search-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="search-clear">
                <FiX />
              </button>
            )}
          </div>
          <button
            className={`filter-toggle ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter /> Filter
          </button>
          <select
            className="role-filter-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="superadmin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="employee">Employee</option>
          </select>
          {(search || roleFilter !== "all") && (
            <button onClick={clearFilters} className="clear-filters-btn">
              <FiX /> Clear
            </button>
          )}
        </div>
        <button className="add-staff-btn" onClick={() => setShowCreate(true)}>
          <FiUserPlus /> Add Staff
        </button>
      </div>

      {/* Result Count */}
      {!loading && (
        <div className="result-count">
          Showing {filteredStaff.length} of {staff.length} staff members
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="staff-loading">
          <LoadingSpinner size="lg" text="Loading staff members..." />
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredStaff.length === 0 && (
        <EmptyState
          icon={FiUserPlus}
          title="No staff members found"
          description={
            search || roleFilter !== "all"
              ? "Try adjusting your search or filter criteria"
              : "Get started by adding your first staff member"
          }
          action={
            search || roleFilter !== "all"
              ? { label: "Clear filters", onClick: clearFilters }
              : { label: "Add Staff", onClick: () => setShowCreate(true) }
          }
        />
      )}

      {/* Staff Grid */}
      {!loading && filteredStaff.length > 0 && (
        <div className="staff-grid-enhanced">
          {filteredStaff.map((member) => (
            <StaffCard
              key={member._id}
              member={member}
              currentUser={currentUser}
              assignableRoles={assignableRoles}
              onRevoke={() => handleRevoke(member)}
              onChangeRole={() => setEditStaff({ ...member })}
              onResetPwd={() => setResetPwd({ ...member, newPassword: "" })}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateStaffModal
          toast={toast}
          currentUser={currentUser}
          assignableRoles={assignableRoles}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}

      {editStaff && (
        <ChangeRoleModal
          member={editStaff}
          assignableRoles={assignableRoles}
          toast={toast}
          onClose={() => setEditStaff(null)}
          onSaved={() => {
            setEditStaff(null);
            load();
          }}
        />
      )}

      {resetPwd && (
        <ResetPasswordModal
          member={resetPwd}
          toast={toast}
          onClose={() => setResetPwd(null)}
          onSaved={() => {
            setResetPwd(null);
            load();
          }}
        />
      )}

      <style jsx>{`
        .staff-container-enhanced {
          padding: 0 4px 32px;
          animation: fadeIn 0.35s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Header */
        .staff-header-enhanced {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 20px;
        }

        .header-title-section {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .header-icon {
          width: 52px;
          height: 52px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          color: white;
        }

        .header-title {
          font-size: 26px;
          font-weight: 700;
          color: var(--text);
          letter-spacing: -0.02em;
          margin-bottom: 6px;
        }

        .header-subtitle {
          font-size: 14px;
          color: var(--text-muted);
        }

        .header-stats {
          display: flex;
          gap: 12px;
        }

        .stat-card-mini {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 16px;
          background: var(--surface);
          border-radius: 14px;
          border: 1px solid var(--border-light);
        }

        .stat-card-mini svg {
          font-size: 20px;
          color: var(--primary);
        }
        .stat-card-mini.superadmin svg {
          color: #f59e0b;
        }
        .stat-card-mini.admin svg {
          color: #6366f1;
        }
        .stat-card-mini.employee svg {
          color: #10b981;
        }

        .stat-card-mini div {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: 18px;
          font-weight: 700;
          color: var(--text);
        }

        .stat-label {
          font-size: 10px;
          color: var(--text-muted);
        }

        /* Toolbar */
        .staff-toolbar-enhanced {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 20px;
        }

        .toolbar-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .search-wrapper {
          position: relative;
          min-width: 280px;
        }

        .search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
        }

        .search-input {
          width: 100%;
          padding: 10px 36px 10px 42px;
          border: 1.5px solid var(--border-light);
          border-radius: 12px;
          font-size: 13px;
          background: var(--surface);
        }

        .search-input:focus {
          outline: none;
          border-color: var(--primary);
        }

        .search-clear {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-muted);
        }

        .filter-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 16px;
          border: 1.5px solid var(--border-light);
          border-radius: 10px;
          background: var(--surface);
          cursor: pointer;
        }

        .filter-toggle.active {
          border-color: var(--primary);
          color: var(--primary);
        }

        .role-filter-select {
          padding: 9px 36px 9px 14px;
          border: 1.5px solid var(--border-light);
          border-radius: 10px;
          background: var(--surface);
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
        }

        .clear-filters-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: transparent;
          border: 1px solid var(--border-light);
          border-radius: 10px;
          font-size: 12px;
          cursor: pointer;
        }

        .add-staff-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 22px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border: none;
          border-radius: 12px;
          color: white;
          font-weight: 600;
          cursor: pointer;
        }

        .result-count {
          margin-bottom: 16px;
          font-size: 13px;
          color: var(--text-muted);
        }

        .staff-loading {
          display: flex;
          justify-content: center;
          padding: 60px;
        }

        /* Staff Grid */
        .staff-grid-enhanced {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 20px;
        }

        /* Staff Card */
        .staff-card-enhanced {
          background: var(--surface);
          border: 1px solid var(--border-light);
          border-radius: 20px;
          overflow: hidden;
          transition: all 0.25s ease;
        }

        .staff-card-enhanced:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
          border-color: rgba(99, 102, 241, 0.2);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: linear-gradient(
            135deg,
            rgba(99, 102, 241, 0.04),
            rgba(139, 92, 246, 0.02)
          );
          border-bottom: 1px solid var(--border-light);
        }

        .avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          font-weight: 800;
          color: white;
          flex-shrink: 0;
        }

        .avatar.superadmin {
          background: linear-gradient(135deg, #f59e0b, #f97316);
        }
        .avatar.admin {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
        }
        .avatar.employee {
          background: linear-gradient(135deg, #10b981, #34d399);
        }

        .user-info {
          flex: 1;
        }

        .user-name {
          font-size: 18px;
          font-weight: 700;
          color: var(--text);
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .self-badge {
          font-size: 10px;
          background: rgba(99, 102, 241, 0.1);
          color: var(--primary);
          padding: 2px 8px;
          border-radius: 20px;
          font-weight: 600;
        }

        .user-email {
          font-size: 13px;
          color: var(--text-muted);
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .user-phone {
          font-size: 12px;
          color: var(--text-muted);
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .joined-date {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: var(--text-muted);
          margin-top: 8px;
        }

        .card-body {
          padding: 16px 20px;
        }

        .role-section {
          margin-bottom: 16px;
        }

        .section-label {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          color: var(--text-muted);
          margin-bottom: 8px;
        }

        .permissions-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }

        .card-actions {
          display: flex;
          gap: 8px;
          padding: 16px 20px;
          border-top: 1px solid var(--border-light);
          background: var(--surface-2);
        }

        .action-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          border: 1px solid var(--border-light);
          background: var(--surface);
          transition: all 0.2s;
        }

        .action-btn.role:hover {
          background: rgba(99, 102, 241, 0.1);
          border-color: var(--primary);
          color: var(--primary);
        }

        .action-btn.password:hover {
          background: rgba(245, 158, 11, 0.1);
          border-color: #f59e0b;
          color: #f59e0b;
        }

        .action-btn.revoke:hover {
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
          color: #ef4444;
        }

        /* Role Badge */
        .role-badge-enhanced {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .role-badge-enhanced.superadmin {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .role-badge-enhanced.admin {
          background: rgba(99, 102, 241, 0.1);
          color: #6366f1;
        }

        .role-badge-enhanced.employee {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .role-badge-enhanced.sm .role-icon {
          font-size: 10px;
        }
        .role-badge-enhanced.sm .role-label {
          font-size: 11px;
        }

        /* Permission Badge */
        .permission-badge-enhanced {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          background: rgba(99, 102, 241, 0.08);
          border-radius: 20px;
          font-size: 11px;
          color: var(--text-secondary);
        }

        /* Modal Styles */
        .staff-modal-enhanced {
          padding: 8px 0;
        }

        .modal-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 24px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-muted);
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
          font-size: 14px;
        }

        .form-input {
          width: 100%;
          padding: 11px 14px 11px 38px;
          border: 1.5px solid var(--border-light);
          border-radius: 12px;
          font-size: 13px;
          background: var(--surface);
          color: black;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--primary);
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-muted);
        }

        .role-description {
          padding: 12px 16px;
          border-radius: 12px;
          margin-bottom: 20px;
          font-size: 12px;
        }

        .role-options {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 8px;
        }

        .role-option {
          flex: 1;
          min-width: 100px;
          padding: 12px;
          border: 2px solid var(--border-light);
          border-radius: 12px;
          background: var(--surface);
          cursor: pointer;
          text-align: center;
          transition: all 0.2s;
        }

        .role-option.active {
          border-color: var(--primary);
          background: rgba(99, 102, 241, 0.05);
        }

        .role-option-icon {
          font-size: 24px;
          margin-bottom: 6px;
          display: block;
        }

        .role-option-label {
          font-size: 13px;
          font-weight: 600;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 20px;
          border-top: 1px solid var(--border-light);
        }

        .btn-cancel {
          padding: 10px 20px;
          background: transparent;
          border: 1.5px solid var(--border-light);
          border-radius: 10px;
          cursor: pointer;
          font-weight: 500;
        }

        .btn-save {
          padding: 10px 24px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border: none;
          border-radius: 10px;
          color: white;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .staff-header-enhanced {
            flex-direction: column;
          }

          .header-stats {
            width: 100%;
            justify-content: space-between;
          }

          .stat-card-mini {
            flex: 1;
            justify-content: center;
          }

          .toolbar-left {
            flex-direction: column;
            align-items: stretch;
            width: 100%;
          }

          .search-wrapper {
            width: 100%;
          }

          .staff-toolbar-enhanced {
            flex-direction: column;
          }

          .add-staff-btn {
            width: 100%;
            justify-content: center;
          }

          .staff-grid-enhanced {
            grid-template-columns: 1fr;
          }

          .modal-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .role-options {
            flex-direction: column;
          }
        }

        @media (max-width: 480px) {
          .header-title-section {
            width: 100%;
          }

          .card-header {
            flex-direction: column;
            text-align: center;
          }

          .card-actions {
            flex-direction: column;
          }

          .action-btn {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}

// Staff Card Component
function StaffCard({
  member,
  currentUser,
  assignableRoles,
  onRevoke,
  onChangeRole,
  onResetPwd,
}) {
  const canModify =
    ROLE_HIERARCHY[currentUser?.role] > ROLE_HIERARCHY[member.role];
  const isSelf = member._id === currentUser?._id;
  const roleMeta = ROLE_META[member.role];

  return (
    <div className="staff-card-enhanced">
      <div className="card-header">
        <div className={`avatar ${member.role}`}>
          {member.name?.charAt(0)?.toUpperCase()}
        </div>
        <div className="user-info">
          <div className="user-name">
            {member.name}
            {isSelf && <span className="self-badge">You</span>}
          </div>
          <div className="user-email">
            <FiMail size={12} /> {member.email}
          </div>
          {member.phone && (
            <div className="user-phone">
              <FiPhone size={12} /> {member.phone}
            </div>
          )}
          <div className="joined-date">
            <FiCalendar size={10} />
            Joined{" "}
            {new Date(member.createdAt).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </div>
        </div>
        <RoleBadge role={member.role} />
      </div>

      <div className="card-body">
        <div className="role-section">
          <div className="section-label">Role Description</div>
          <p
            style={{ fontSize: 12, color: "var(--text-secondary)", margin: 0 }}
          >
            {roleMeta?.description}
          </p>
          <div className="permissions-list">
            {roleMeta?.permissions?.map((perm, idx) => (
              <PermissionBadge key={idx} permission={perm} />
            ))}
          </div>
        </div>
      </div>

      {!isSelf && canModify && (
        <div className="card-actions">
          <button className="action-btn role" onClick={onChangeRole}>
            <FiShield /> Change Role
          </button>
          <button className="action-btn password" onClick={onResetPwd}>
            <FiKey /> Reset Password
          </button>
          <button className="action-btn revoke" onClick={onRevoke}>
            <FiUserX /> Revoke
          </button>
        </div>
      )}
    </div>
  );
}

// Create Staff Modal
function CreateStaffModal({ toast, assignableRoles, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "employee",
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast("Name, email and password are required.", "error");
      return;
    }
    if (form.password.length < 6) {
      toast("Password must be at least 6 characters.", "error");
      return;
    }
    setSaving(true);
    try {
      await aPost("/users/staff/create", form);
      toast(`${form.name} added as ${ROLE_META[form.role]?.label} ✓`);
      onCreated();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const roleMeta = ROLE_META[form.role];

  return (
    <Modal title="Add Staff Member" onClose={onClose} size="md">
      <div className="staff-modal-enhanced">
        <div className="modal-grid">
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <div className="input-wrapper">
              <FiUser className="input-icon" />
              <input
                className="form-input"
                placeholder="John Doe"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Phone (Optional)</label>
            <div className="input-wrapper">
              <FiPhone className="input-icon" />
              <input
                className="form-input"
                placeholder="9876543210"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Email Address *</label>
          <div className="input-wrapper">
            <FiMail className="input-icon" />
            <input
              className="form-input"
              type="email"
              placeholder="staff@company.com"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Password *</label>
          <div className="input-wrapper">
            <FiLock className="input-icon" />
            <input
              className="form-input"
              type={showPassword ? "text" : "password"}
              placeholder="Min 6 characters"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
            />
            <button
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiX size={14} /> : <FiKey size={14} />}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Assign Role *</label>
          <div className="role-options">
            {assignableRoles.map((r) => {
              const m = ROLE_META[r];
              const selected = form.role === r;
              return (
                <button
                  key={r}
                  className={`role-option ${selected ? "active" : ""}`}
                  onClick={() => set("role", r)}
                  type="button"
                >
                  <span className="role-option-icon">{m.icon}</span>
                  <span className="role-option-label">{m.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div
          className={`role-description`}
          style={{
            background: roleMeta?.bg,
            borderColor: `${roleMeta?.color}30`,
          }}
        >
          <strong style={{ color: roleMeta?.color }}>{roleMeta?.label}</strong>:{" "}
          {roleMeta?.description}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleCreate} disabled={saving}>
            {saving ? (
              <div className="spinner-small" />
            ) : (
              <FiUserPlus size={14} />
            )}
            {saving ? "Creating..." : "Create Staff"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Change Role Modal
function ChangeRoleModal({ member, assignableRoles, toast, onClose, onSaved }) {
  const [role, setRole] = useState(member.role);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await aPut(`/users/staff/${member._id}/role`, { role });
      toast(`Role updated to ${ROLE_META[role]?.label} ✓`);
      onSaved();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const selectedMeta = ROLE_META[role];
  const currentMeta = ROLE_META[member.role];

  return (
    <Modal title="Change Staff Role" onClose={onClose} size="md">
      <div className="staff-modal-enhanced">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: 16,
            background: "var(--surface-2)",
            borderRadius: 16,
            marginBottom: 24,
          }}
        >
          <div
            className={`avatar ${member.role}`}
            style={{ width: 48, height: 48, fontSize: 20 }}
          >
            {member.name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{member.name}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {member.email}
            </div>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <RoleBadge role={member.role} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Current Role</label>
          <div
            style={{
              padding: "10px 14px",
              background: currentMeta.bg,
              borderRadius: 10,
              color: currentMeta.color,
              fontWeight: 600,
            }}
          >
            {currentMeta.icon} {currentMeta.label}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Select New Role</label>
          <div className="role-options">
            {assignableRoles.map((r) => {
              const m = ROLE_META[r];
              const selected = role === r;
              return (
                <button
                  key={r}
                  className={`role-option ${selected ? "active" : ""}`}
                  onClick={() => setRole(r)}
                  type="button"
                >
                  <span className="role-option-icon">{m.icon}</span>
                  <span className="role-option-label">{m.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div
          className="role-description"
          style={{
            background: selectedMeta.bg,
            borderColor: `${selectedMeta.color}30`,
          }}
        >
          <strong style={{ color: selectedMeta.color }}>
            {selectedMeta.label}
          </strong>
          : {selectedMeta.description}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving || role === member.role}
          >
            {saving ? (
              <div className="spinner-small" />
            ) : (
              <FiShield size={14} />
            )}
            {saving ? "Updating..." : "Update Role"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Reset Password Modal
function ResetPasswordModal({ member, toast, onClose, onSaved }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (password.length < 6) {
      toast("Password must be at least 6 characters.", "error");
      return;
    }
    if (password !== confirmPassword) {
      toast("Passwords do not match.", "error");
      return;
    }
    setSaving(true);
    try {
      await aPut(`/users/staff/${member._id}/reset-password`, { password });
      toast(`Password reset for ${member.name} ✓`);
      onSaved();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Reset Password" onClose={onClose} size="sm">
      <div className="staff-modal-enhanced">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: 12,
            background: "var(--surface-2)",
            borderRadius: 12,
            marginBottom: 20,
          }}
        >
          <div
            className={`avatar ${member.role}`}
            style={{ width: 40, height: 40, fontSize: 16 }}
          >
            {member.name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{member.name}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {member.email}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">New Password</label>
          <div className="input-wrapper">
            <FiLock className="input-icon" />
            <input
              className="form-input"
              type={showPassword ? "text" : "password"}
              placeholder="Min 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Confirm Password</label>
          <div className="input-wrapper">
            <FiLock className="input-icon" />
            <input
              className="form-input"
              type={showPassword ? "text" : "password"}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiX size={14} /> : <FiKey size={14} />}
            </button>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving || password.length < 6}
          >
            {saving ? <div className="spinner-small" /> : <FiKey size={14} />}
            {saving ? "Resetting..." : "Reset Password"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
